import { useCallback, useMemo } from 'react';
import { TFunction } from 'i18next';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DocumentationValidationStatusEnum,
  FormTemplateQuestionField,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import DocumentationPreviewComponent from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import IconButtonWithDropDown from '@lib/components/IconButtonWithDropDown/IconButtonWithDropDown';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import { PlanState } from '@lib/enums/plan';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useDocumentActions from 'components/FormTemplateBuilder/utils/useDocumentActions';
import useFormDocumentsMap from 'components/FormTemplateBuilder/utils/FormDocumentsMapContext';
import getDocumentActionsNavItems from 'components/FormTemplateBuilder/utils/getDocumentActionsNavItems';
import styles from '../QuestionFieldPreviewByKind.module.scss';

interface DocumentPreviewProps {
  options: FormTemplateQuestionField[typeof FormTemplateFieldNames.Options];
  t: TFunction<'translation', undefined>;
}

export default function DocumentPreview({ options, t }: DocumentPreviewProps) {
  const { onViewDocument, onVersionNotes, onDetails, renderDocumentModals } =
    useDocumentActions();
  const {
    getUserAccessByCompany,
    handleOpenPlanModal,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  const onUpgradePlan = useCallback(() => {
    handleOpenPlanModal(PlanState.Locked, [
      CompanyPlanUpgradeFeatureEnum.Documentation,
    ]);
  }, [handleOpenPlanModal]);

  const docOptions = useMemo(
    () =>
      (options || [])
        .filter((opt) => opt.optionDocumentation?.documentationId)
        .map((opt) => ({
          docId: opt.optionDocumentation?.documentationId as string,
          requiredToRead: opt.optionDocumentation?.requiredToRead ?? true,
        })),
    [options],
  );
  const docIds = useMemo(() => docOptions.map((o) => o.docId), [docOptions]);
  const hasDocuments = docIds.length > 0;
  const { documentsMap, loading } = useFormDocumentsMap(docIds);

  if (isViewLocked && isFullAdmin) {
    return (
      <SupportiveMessage
        text={t('document-question-type-unavailable')}
        color={SupportiveMessageColor.Yellow}
        type={SupportiveMessageType.FilledWithLeftBorder}
        actions={
          <Button
            buttonText={t('upgrade-plan')}
            buttonType={ButtonTypes.primaryFilled}
            onClick={onUpgradePlan}
          />
        }
      />
    );
  }

  if (!hasDocuments) {
    return (
      <SupportiveMessage
        text={t('no-document-attached')}
        color={SupportiveMessageColor.Yellow}
        type={SupportiveMessageType.FilledWithLeftBorder}
      />
    );
  }

  return (
    <>
      <div className={styles.documentsList}>
        {docOptions.map(({ docId, requiredToRead }) => {
          const doc = documentsMap.get(docId);
          if (!doc) {
            if (loading) return null;
            return (
              <SupportiveMessage
                key={docId}
                text={t('document-unavailable')}
                leftIconSymbolsOutlined
                color={SupportiveMessageColor.Gray}
                hasBottomSpace={false}
              />
            );
          }
          return (
            <DocumentationPreviewComponent
              key={docId}
              attachment={doc.currentVersion?.attachmentAttached}
              view={DocumentationPreviewEnum.DocumentRowCard}
              customName={doc.name || docId}
              notes={doc.currentVersion?.notes || undefined}
              isFavorite={!!doc.favorited}
              isRequired={requiredToRead}
              notValidated={
                doc.validationStatus !==
                DocumentationValidationStatusEnum.Validated
              }
              actions={
                <IconButtonWithDropDown
                  buttonProps={{ icon: 'more_vert' }}
                  popoverProps={{
                    width: 220,
                    placement: 'bottomRight',
                  }}
                  navItems={getDocumentActionsNavItems({
                    doc,
                    onViewDocument,
                    onVersionNotes,
                    onDetails,
                    t,
                  })}
                />
              }
            />
          );
        })}
      </div>
      {renderDocumentModals}
    </>
  );
}
